import React, { useState } from "react";
import { motion } from "motion/react";
import { Send, Mail, Phone, MapPin, User, Calendar, Users, MessageSquare, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"./ui/select";
import { EMAIL, WHATSAPP_NUMBER, WHATSAPP_LINK, WHATSAPP_MESSAGE } from "../data/mock";
import { toast } from "sonner";

const ContatoSection = () => {
  const [formData, setFormData] = useState({
    nome: "",
    dataViagem: "",
    pessoas: "",
    interesse: "",
    mensagem: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, interesse: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Create WhatsApp message with form data
    const message = `Olá! Meu nome é ${formData.nome}.\n\nData da viagem: ${formData.dataViagem}\nQuantas pessoas: ${formData.pessoas}\nInteresse: ${formData.interesse}\n\nMensagem: ${formData.mensagem}`;
    const whatsappUrl = `${WHATSAPP_LINK}?text=${encodeURIComponent(message)}`;

    toast.success("Formulário enviado! Redirecionando para WhatsApp...");

    setTimeout(() => {
      window.open(whatsappUrl, "_blank");
    }, 1000);

    setIsSubmitting(false);
    setFormData({
      nome: "",
      dataViagem: "",
      pessoas: "",
      interesse: "",
      mensagem: ""
    });
  };

  const whatsappUrl = `${WHATSAPP_LINK}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

  return (
    <section id="contato" className="py-20 lg:py-28 gradient-warm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16">

          <span className="inline-block text-[hsl(var(--coral))] text-sm font-semibold uppercase tracking-wider mb-4">
            Entre em Contato
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Vamos planejar sua viagem?
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Preencha o formulário ou entre em contato diretamente pelo WhatsApp.
            Respondemos rapidamente!
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h3 className="font-display text-xl font-semibold text-gray-900 mb-6">
                Envie sua solicitação
              </h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4" />
                    Nome
                  </label>
                  <Input
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    placeholder="Seu nome completo"
                    className="h-12 rounded-xl"
                    required />

                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="w-4 h-4" />
                      Data da viagem
                    </label>
                    <Input
                      name="dataViagem"
                      type="date"
                      value={formData.dataViagem}
                      onChange={handleChange}
                      className="h-12 rounded-xl"
                      required />

                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <Users className="w-4 h-4" />
                      Quantas pessoas
                    </label>
                    <Input
                      name="pessoas"
                      type="number"
                      min="1"
                      value={formData.pessoas}
                      onChange={handleChange}
                      placeholder="Ex: 2"
                      className="h-12 rounded-xl"
                      required />

                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    O que procura?
                  </label>
                  <Select onValueChange={handleSelectChange} value={formData.interesse}>
                    <SelectTrigger className="h-12 rounded-xl">
                      <SelectValue placeholder="Selecione uma opção" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="suite">Suíte</SelectItem>
                      <SelectItem value="passeio">Passeio</SelectItem>
                      <SelectItem value="evento">Evento</SelectItem>
                      <SelectItem value="gastronomia">Gastronomia</SelectItem>
                      <SelectItem value="motorista">Motorista</SelectItem>
                      <SelectItem value="pacote_completo">Pacote Completo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <MessageSquare className="w-4 h-4" />
                    Mensagem
                  </label>
                  <Textarea
                    name="mensagem"
                    value={formData.mensagem}
                    onChange={handleChange}
                    placeholder="Conte-nos mais sobre o que você procura..."
                    className="min-h-[120px] rounded-xl"
                    required />

                </div>

                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[hsl(var(--coral))] hover:bg-[hsl(var(--warm-orange))] text-white rounded-full py-4 h-auto font-semibold text-lg gap-2">

                    {isSubmitting ?
                    <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Enviando...
                      </> :

                    <>
                        <Send className="w-5 h-5" />
                        Enviar
                      </>
                    }
                  </Button>
                </motion.div>
              </form>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col justify-center">

            <div className="space-y-8">
              {/* WhatsApp Card */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-[hsl(var(--whatsapp))]">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[hsl(var(--whatsapp))/0.1] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[hsl(var(--whatsapp))]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">WhatsApp</h4>
                    <p className="text-gray-600 mb-3">Resposta rápida e personalizada</p>
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[hsl(var(--whatsapp))] font-semibold hover:underline">

                      {WHATSAPP_NUMBER}
                    </a>
                  </div>
                </div>
              </div>

              {/* Email Card */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-[hsl(var(--coral))]">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[hsl(var(--coral))/0.1] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[hsl(var(--coral))]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                    <p className="text-gray-600 mb-3">Para solicitações e orçamentos</p>
                    <a
                      href={`mailto:${EMAIL}`}
                      className="inline-flex items-center gap-2 text-[hsl(var(--coral))] font-semibold hover:underline">

                      {EMAIL}
                    </a>
                  </div>
                </div>
              </div>

              {/* Location Card */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-[hsl(var(--golden))]">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[hsl(var(--golden))/0.1] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[hsl(var(--golden))]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Localização</h4>
                    <p className="text-gray-600">Av Vice Presidente José de Alencar n 1400 bl 2 ap 306 - Rio de Janeiro, RJ - Brasil - CEP 22775-033</p>
                    <p className="text-gray-500 text-sm mt-1">Atendemos toda a cidade</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>);

};

export default ContatoSection;